import { apiService, type ApiResponse } from './api';
import type { AxiosProgressEvent } from 'axios';

export interface AppraisalEmployee {
  sr_no: number;
  emp_id: string;
  emp_name: string;
  emp_appraisal_status: string | null;
  emp_department: string | null;
  emp_designation: string | null;
  emp_email: string;
  can_view: boolean;
}

export interface EmployeeInfo {
  emp_id: string;
  emp_name: string;
  emp_manager: string;
  emp_department: string;
  emp_designation: string;
  emp_manager_rating: number | null;
  emp_feedback_rating: number | null;
  emp_overall_rating: number | null;
  emp_hod_rating?: number | null;
  emp_hr_rating?: number | null;
  admin_status?: string; // 'pending' | 'submitted'
}

export interface EmployeeTimelineStage {
  stage_label: string;
  stage_timestamp: string | null;
  stage_status: string;
  action_by?: string | null;
  remarks?: string | null;
  relationship_type?: string | null;
}

export interface EmployeeDetailsData {
  employee_info: EmployeeInfo;
  employee_timeline: EmployeeTimelineStage[];
}

export interface SelfAppraisalOKR {
  objective: string;
  target: string;
  eyr: string;
  rating: number;
}

export interface SelfAppraisalItem {
  id: number;
  okrs: SelfAppraisalOKR[];
  status: string;
  manager_comments?: string | null;
}

export interface SelfAppraisalData {
  self_appraisal_list: SelfAppraisalItem[];
  manager_comments?: string | null;
}

export interface ReviewerInfo {
  email: string;
  emp_id: string;
  name: string;
}

export interface ReviewFeedback {
  constructive: string;
  appreciative: string;
}

export interface ReviewCompetency {
  solution_focused: number;
  adapt_to_win: number;
  courage_to_act: number;
  act_for_enterprise: number;
  own_it: number;
}

export interface ReviewDetail {
  review_id: number;
  reviewer: ReviewerInfo;
  submitted_at: string;
  status: string;
  feedback: ReviewFeedback;
  competency: ReviewCompetency;
  relationship_type: string;
}

export interface SuperiorReviewData {
  data: ReviewDetail[];
}

export interface NonSuperiorReviewData {
  data: ReviewDetail[];
}

class AppraisalAdminService {
  private readonly listEndpoint = '/api/v1/feedback/admin/employee/list/';
  private readonly uploadEndpoint = '/api/v1/feedback/admin/upload/';
  private readonly detailsEndpoint = '/api/v1/feedback/admin/employee/details/';
  private readonly selfAppraisalOKREndpoint = '/api/v1/feedback/admin/self_appraisal/okr/';
  private readonly superiorReviewEndpoint = '/api/v1/feedback/admin/superior_review_details/';
  private readonly nonSuperiorReviewEndpoint = '/api/v1/feedback/admin/non_superior_review_details/';
  private readonly hodReviewEndpoint = '/api/v1/feedback/admin/hod_review_details/';
  private readonly exportEndpoint = '/api/v1/feedback/admin/export/';

  /**
   * Fetch list of employees for admin view
   */
  async getEmployeeList(email: string): Promise<ApiResponse<AppraisalEmployee[]>> {
    try {
      const encoded = encodeURIComponent(email);
      const response = await apiService.get<AppraisalEmployee[]>(`${this.listEndpoint}?email=${encoded}`);
      return response;
    } catch (error) {
      console.error('Error fetching appraisal admin employee list:', error);
      throw error;
    }
  }

  /**
   * Fetch employee details for admin view
   */
  async getEmployeeDetails(email: string, empId: string): Promise<ApiResponse<EmployeeDetailsData>> {
    try {
      const encodedEmail = encodeURIComponent(email);
      const encodedEmpId = encodeURIComponent(empId);
      const response = await apiService.get<EmployeeDetailsData>(`${this.detailsEndpoint}?email=${encodedEmail}&emp_id=${encodedEmpId}`);
      return response;
    } catch (error) {
      console.error('Error fetching employee details:', error);
      throw error;
    }
  }

  /**
   * Fetch self appraisal OKR data for admin view
   */
  async getSelfAppraisalOKR(email: string, emp_id:string): Promise<ApiResponse<SelfAppraisalData>> {
    try {
      const response = await apiService.get<SelfAppraisalData>(`${this.selfAppraisalOKREndpoint}`,{params: { email: email, emp_id: emp_id } });
      return response;
    } catch (error) {
      console.error('Error fetching self appraisal OKR data:', error);
      throw error;
    }
  }

  /**
   * Upload CSV or Excel file (FormData key: file)
   * Supports .csv, .xlsx, and .xls formats
   */
  async uploadEmployeeFile(
    email: string,
    file: File,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<ApiResponse<unknown>> {
    try {
      const encoded = encodeURIComponent(email);
      const url = `${this.uploadEndpoint}?email=${encoded}`;
  const response = await apiService.uploadFile<unknown>(url, file, onUploadProgress);
      return response;
    } catch (error) {
      console.error('Error uploading employee file:', error);
      throw error;
    }
  }

  /**
   * @deprecated Use uploadEmployeeFile instead. This method is kept for backward compatibility.
   */
  async uploadEmployeeCSV(
    email: string,
    file: File,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<ApiResponse<unknown>> {
    return this.uploadEmployeeFile(email, file, onUploadProgress);
  }

  /**
   * Fetch superior review details for admin view
   */
  async getSuperiorReviewDetails(email: string, emp_id: string): Promise<ApiResponse<SuperiorReviewData>> {
    try {
      const encodedEmail = encodeURIComponent(email);
      const encodedEmpId = encodeURIComponent(emp_id);
      const response = await apiService.get<SuperiorReviewData>(`${this.superiorReviewEndpoint}?email=${encodedEmail}&emp_id=${encodedEmpId}`);
      return response;
    } catch (error) {
      console.error('Error fetching superior review details:', error);
      throw error;
    }
  }

  /**
   * Fetch non-superior review details for admin view
   */
  async getNonSuperiorReviewDetails(email: string, emp_id: string): Promise<ApiResponse<NonSuperiorReviewData>> {
    try {
      const encodedEmail = encodeURIComponent(email);
      const encodedEmpId = encodeURIComponent(emp_id);
      const response = await apiService.get<NonSuperiorReviewData>(`${this.nonSuperiorReviewEndpoint}?email=${encodedEmail}&emp_id=${encodedEmpId}`);
      return response;
    } catch (error) {
      console.error('Error fetching non-superior review details:', error);
      throw error;
    }
  }

  /**
   * Fetch HOD review details for admin view
   */
  async getHODReviewDetails(email: string, emp_id: string): Promise<ApiResponse<SuperiorReviewData>> {
    try {
      const encodedEmail = encodeURIComponent(email);
      const encodedEmpId = encodeURIComponent(emp_id);
      const response = await apiService.get<SuperiorReviewData>(`${this.hodReviewEndpoint}?email=${encodedEmail}&emp_id=${encodedEmpId}`);
      return response;
    } catch (error) {
      console.error('Error fetching HOD review details:', error);
      throw error;
    }
  }

  /**
   * Export appraisal data for admin - downloads file
   */
  async exportAppraisalData(email: string): Promise<ApiResponse<Blob>> {
    try {
      const encoded = encodeURIComponent(email);
      const response = await apiService.get<Blob>(`${this.exportEndpoint}?email=${encoded}`, {
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      console.error('Error exporting appraisal data:', error);
      throw error;
    }
  }

  /**
   * Helper method to trigger file download from blob response
   */
  downloadExportedFile(blob: Blob, filename: string = 'appraisal_export.csv'): void {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  }

  /**
   * Update HR final rating for an employee
   * PUT /api/v1/feedback/hr_final_review
   */
  async updateHrRating(
    submitterEmail: string,
    employeeId: string,
    hrRating: number
  ): Promise<ApiResponse<any>> {
    try {
      const response = await apiService.put<ApiResponse<any>>(
        '/api/v1/feedback/hr_final_review/',
        {
          submitter_email: submitterEmail,
          emp_id: employeeId,
          hr_rating: hrRating,
        }
      );
      return response;
    } catch (error) {
      console.error('Error updating HR rating:', error);
      throw error;
    }
  }
}

export const appraisalAdminService = new AppraisalAdminService();
export default appraisalAdminService;
